/**
 * MATERIAL REALITY — presentation only.
 *
 * Two backend contracts meet on this surface, and they answer different
 * questions:
 *
 *   material-authority-1        WHAT DELIVERY DATE IS DEFENSIBLE RIGHT NOW,
 *   (read)                      AND ON WHOSE AUTHORITY?
 *
 *   execution-observation-1     WHAT DID SOMEONE ACTUALLY SEE ON SITE?
 *   (write)
 *
 * THE RULE THAT GOVERNS EVERY LINE BELOW: an expectation is not an actual,
 * and arriving is not being verified. The backend ranks its evidence
 * (`backend/services/material_authority.py`) and this module translates that
 * ranking into the words a superintendent already uses. It does not compute a
 * rank, override one, or resolve a conflict — the resolver picks no winner
 * when evidence of equal authority disagrees, and neither do we.
 *
 * WHAT THIS MODULE WILL NEVER DO
 *
 *   * turn a missing quantity into 0 — "not stated" is the answer;
 *   * call a recorded delivery "verified";
 *   * call a supplier's expectation an actual;
 *   * pick a side in a CONFLICT, or rank the two sides for the reader;
 *   * report UNKNOWN as a resolved date, or as an error;
 *   * teach a field user the numeric authority ranks as the normal workflow.
 *
 * Pure and dependency-free, so every rule is testable without a DOM.
 *
 * Contract source: backend/services/material_authority.py and
 * backend/routes/execution_intelligence.py, read on main at 6ec706b.
 */

export const MATERIAL_AUTHORITY_CONTRACT = 'material-authority-1';

/* -------------------------------------------------------------------------
 * THE EVIDENCE LADDER, IN CONSTRUCTION LANGUAGE
 *
 * The backend's `authority` token is the truth; these are its words for a
 * human. `rank` is carried but deliberately NOT rendered in the normal
 * workflow — a field user should never have to learn that 2 outranks 3.
 * ---------------------------------------------------------------------- */

const AUTHORITY_DISPLAY = {
  PHYSICALLY_VERIFIED_DELIVERY: {
    rank: 1,
    label: 'Field verified',
    short: 'Verified',
    meaning: 'Someone stood next to it and swore the quantity was sufficient.',
    /** Non-colour channel, so the ladder reads on a monochrome screen. */
    mark: '★',
    kind: 'ACTUAL',
  },
  APPROVED_RECEIVING_RECORD: {
    rank: 2,
    label: 'Approved receiving evidence',
    short: 'Received',
    meaning: 'A named, approved receiving record says it arrived.',
    mark: '◆',
    kind: 'ACTUAL',
  },
  RECORDED_ACTUAL_DELIVERY: {
    rank: 3,
    label: 'Recorded actual delivery',
    short: 'Delivered',
    meaning:
      'A delivery was recorded against this material. Nobody has verified it.',
    mark: '●',
    kind: 'ACTUAL',
  },
  CONFIRMED_SUPPLIER_EXPECTED_DELIVERY: {
    rank: 4,
    label: 'Supplier committed date',
    short: 'Committed',
    meaning:
      'The supplier committed to this date. Nothing has arrived on it yet.',
    mark: '◐',
    kind: 'EXPECTATION',
  },
  CURRENT_PROCUREMENT_EXPECTED_DELIVERY: {
    rank: 5,
    label: 'Supplier expected date',
    short: 'Expected',
    meaning:
      'The commercial plan expects this date. It is a plan, not an event.',
    mark: '○',
    kind: 'EXPECTATION',
  },
  PROPOSED_OR_INFERRED_DELIVERY: {
    rank: 6,
    label: 'Machine proposal',
    short: 'Proposed',
    meaning:
      'OMNΛS worked this date out. No record states it, and nothing should be planned against it as fact.',
    mark: '?',
    kind: 'PROPOSAL',
  },
};

/**
 * An authority token this frontend does not recognise is shown under its own
 * name and classed UNSTATED. Mapping it onto one we do know would put a claim
 * about evidence strength in the backend's mouth.
 */
export const authorityDisplay = (authority) => {
  const key = typeof authority === 'string' ? authority.trim() : '';
  if (!key) {
    return {
      rank: null,
      label: 'No authority stated',
      short: 'Not stated',
      meaning: 'No record was named as the source of this date.',
      mark: '·',
      kind: 'UNSTATED',
      known: false,
    };
  }
  const known = AUTHORITY_DISPLAY[key];
  if (known) return { ...known, known: true };
  return {
    rank: null,
    label: key
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    short: 'Unrecognised',
    meaning:
      'OMNΛS returned an evidence class this screen does not have words for yet.',
    mark: '?',
    kind: 'UNSTATED',
    known: false,
  };
};

/**
 * Is the date on screen a thing that HAPPENED, or a thing someone EXPECTS?
 *
 * The single most consequential distinction on this surface. A plan read as
 * an actual is how a crew gets sent to install material that is still on a
 * truck.
 */
export const isActualDelivery = (authority) =>
  authorityDisplay(authority).kind === 'ACTUAL';

/* -------------------------------------------------------------------------
 * THE THREE ANSWERS THE RESOLVER CAN GIVE
 * ---------------------------------------------------------------------- */

export const MATERIAL_STATUS = {
  RESOLVED: 'RESOLVED',
  CONFLICT: 'CONFLICT',
  UNKNOWN: 'UNKNOWN',
};

const STATUS_DISPLAY = {
  RESOLVED: {
    label: 'Date established',
    tone: 'settled',
    mark: '✓',
    meaning: 'One record outranks the rest, and this is the date it states.',
  },
  CONFLICT: {
    label: 'Records disagree',
    tone: 'conflict',
    mark: '⇄',
    meaning:
      'Two records of equal authority state different dates. OMNΛS has not chosen between them, and neither should this screen.',
  },
  UNKNOWN: {
    label: 'No date established',
    tone: 'unknown',
    mark: '—',
    meaning:
      'No evidence supports a delivery date. Nothing has been inferred from lead time or an order date.',
  },
};

export const materialStatusDisplay = (status) =>
  STATUS_DISPLAY[status] || {
    label: 'Not stated',
    tone: 'unknown',
    mark: '?',
    meaning: 'The resolver did not state a status for this material.',
  };

/* -------------------------------------------------------------------------
 * READING THE CONTRACT
 * ---------------------------------------------------------------------- */

const isText = (v) => typeof v === 'string' && v.trim().length > 0;
const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);

/**
 * A quantity the backend did not state.
 *
 * `required_qty` is NOT NULL in the schema, but `delivered_qty` and
 * `verified_qty` are nullable and an absent one means NOBODY HAS SAID. Zero
 * is a different claim — it means someone counted none — and this surface
 * must never manufacture it.
 */
export const quantityLabel = (value, uom) => {
  if (!isNum(value)) return 'Not stated';
  const unit = isText(uom) ? ` ${uom.trim()}` : '';
  return `${value}${unit}`;
};

export const formatDate = (iso) => {
  if (!isText(iso)) return 'Not stated';
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    const loose = new Date(iso);
    if (Number.isNaN(loose.getTime())) return 'Not stated';
    return loose.toISOString().slice(0, 10);
  }
  return parsed.toISOString().slice(0, 10);
};

/**
 * One material's delivery authority.
 *
 * @returns {{readable: true, material: object} | {readable: false, reason: string, id: string|null}}
 */
export const readMaterialContract = (raw) => {
  if (raw === null || raw === undefined) {
    return {
      readable: false,
      reason: 'No material payload was returned.',
      id: null,
    };
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      readable: false,
      reason: 'The material payload is not an object.',
      id: null,
    };
  }
  if (!isText(raw.materialStatusId)) {
    return {
      readable: false,
      reason: 'The material payload names no materialStatusId.',
      id: null,
    };
  }

  const status = Object.prototype.hasOwnProperty.call(
    STATUS_DISPLAY,
    raw.status,
  )
    ? raw.status
    : null;
  const authority = isText(raw.authority) ? raw.authority.trim() : null;

  /**
   * The competing records, in the resolver's own order, with NO winner marked.
   * `conflicts` is only populated on a CONFLICT and is exactly the set the
   * backend refused to choose between.
   */
  const conflicts = Array.isArray(raw.conflicts)
    ? raw.conflicts
        .filter((c) => c && typeof c === 'object')
        .map((c) => ({
          authority: isText(c.authority) ? c.authority : null,
          authorityDisplay: authorityDisplay(c.authority),
          deliveryDate: isText(c.delivery_date) ? c.delivery_date : null,
          deliveryDateLabel: formatDate(c.delivery_date),
          sourceType: c.source_record?.type || null,
          sourceId: c.source_record?.id || null,
          detail: isText(c.detail) ? c.detail : null,
        }))
    : [];

  return {
    readable: true,
    material: {
      id: raw.materialStatusId,
      procurementItemId: isText(raw.procurementItemId)
        ? raw.procurementItemId
        : null,
      name: isText(raw.materialName) ? raw.materialName : null,
      /** A nameless material is listed by nothing we invented. */
      nameLabel: isText(raw.materialName)
        ? raw.materialName
        : 'Unnamed material',
      status,
      statusDisplay: materialStatusDisplay(status),
      deliveryDate: isText(raw.deliveryDate) ? raw.deliveryDate : null,
      deliveryDateLabel: formatDate(raw.deliveryDate),
      authority,
      authorityDisplay: authorityDisplay(authority),
      /** Carried, never rendered in the normal workflow. Diagnostics only. */
      authorityRank: isNum(raw.authorityRank) ? raw.authorityRank : null,
      sourceRecordType: isText(raw.sourceRecordType)
        ? raw.sourceRecordType
        : null,
      sourceRecordId: isText(raw.sourceRecordId) ? raw.sourceRecordId : null,
      unknownReason: isText(raw.unknownReason) ? raw.unknownReason : null,
      conflicts,
      warnings: Array.isArray(raw.warnings) ? raw.warnings.filter(isText) : [],
      scheduleConstraint: {
        resolved: raw.scheduleConstraint?.resolved === true,
        earliestStart: isText(raw.scheduleConstraint?.earliestStart)
          ? raw.scheduleConstraint.earliestStart
          : null,
        detail: isText(raw.scheduleConstraint?.detail)
          ? raw.scheduleConstraint.detail
          : null,
      },
    },
  };
};

/**
 * The project's materials.
 *
 * An unreadable ENVELOPE is refused whole — reporting it as an empty list
 * would claim this project tracks no materials, which is a claim about the
 * job. An unreadable ROW is counted rather than dropped.
 */
export const readMaterialListContract = (raw) => {
  if (raw === null || raw === undefined) {
    return {
      readable: false,
      reason: 'No response was returned.',
      materials: [],
      unreadable: [],
    };
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    return {
      readable: false,
      reason:
        'The material authority payload is not an object. MALFORMED IS NOT EMPTY.',
      materials: [],
      unreadable: [],
    };
  }
  if (!Array.isArray(raw.materials)) {
    return {
      readable: false,
      reason:
        'The material authority payload carries no `materials` array. MALFORMED IS NOT EMPTY.',
      materials: [],
      unreadable: [],
    };
  }

  const materials = [];
  const unreadable = [];
  const seen = new Set();
  for (const row of raw.materials) {
    const read = readMaterialContract(row);
    if (!read.readable) {
      unreadable.push({ id: read.id, reason: read.reason });
      continue;
    }
    if (seen.has(read.material.id)) {
      unreadable.push({
        id: read.material.id,
        reason: 'A second row repeats this material id.',
      });
      continue;
    }
    seen.add(read.material.id);
    materials.push(read.material);
  }

  /**
   * The backend's OWN counts are kept separate from what could be read, and
   * from what the list happens to contain: `conflictsOnly=true` returns a
   * FILTERED list beside UNFILTERED counts, so deriving "12 materials" from
   * `materials.length` would be wrong by construction.
   */
  return {
    readable: true,
    reason: null,
    materials,
    unreadable,
    counts: {
      total: isNum(raw.count) ? raw.count : null,
      resolved: isNum(raw.resolvedCount) ? raw.resolvedCount : null,
      conflict: isNum(raw.conflictCount) ? raw.conflictCount : null,
      unknown: isNum(raw.unknownCount) ? raw.unknownCount : null,
    },
    truncated: raw.truncated === true,
  };
};

/**
 * The one line a project lead reads first.
 *
 * Deliberately NOT a percentage or a score. "9 of 15 established, 3 disagree,
 * 3 unknown" is six dates nobody should be planning against, said plainly.
 */
export const authoritySummary = (counts) => {
  const c = counts || {};
  if (!isNum(c.total)) {
    return {
      stated: false,
      headline: 'The material count was not stated.',
      parts: [],
    };
  }
  const parts = [];
  if (isNum(c.resolved)) parts.push(`${c.resolved} established`);
  if (isNum(c.conflict)) parts.push(`${c.conflict} in disagreement`);
  if (isNum(c.unknown)) parts.push(`${c.unknown} with no date`);
  const unplannable =
    (isNum(c.conflict) ? c.conflict : 0) + (isNum(c.unknown) ? c.unknown : 0);
  return {
    stated: true,
    total: c.total,
    unplannable,
    headline:
      unplannable > 0
        ? `${unplannable} of ${c.total} materials have no date you can plan against.`
        : `All ${c.total} materials have an established delivery date.`,
    parts,
  };
};

/** Sorting that puts what a person must act on first, without scoring it. */
const STATUS_ORDER = { CONFLICT: 0, UNKNOWN: 1, RESOLVED: 2 };

export const orderMaterials = (materials) => {
  const rows = Array.isArray(materials) ? materials.slice() : [];
  return rows.sort((a, b) => {
    const sa = STATUS_ORDER[a?.status] ?? 3;
    const sb = STATUS_ORDER[b?.status] ?? 3;
    if (sa !== sb) return sa - sb;
    return String(a?.nameLabel || '').localeCompare(String(b?.nameLabel || ''));
  });
};

export const filterMaterials = (
  materials,
  { query = '', status = 'all' } = {},
) => {
  const rows = Array.isArray(materials) ? materials : [];
  const needle = typeof query === 'string' ? query.trim().toLowerCase() : '';
  return rows.filter((m) => {
    if (status !== 'all' && m?.status !== status) return false;
    if (!needle) return true;
    return [m?.name, m?.authorityDisplay?.label, m?.statusDisplay?.label]
      .filter(isText)
      .join(' ')
      .toLowerCase()
      .includes(needle);
  });
};

/* -------------------------------------------------------------------------
 * WHAT HAPPENED TO WHAT THE FIELD RECORDED
 *
 * The doctrine, from backend/routes/execution_observation.py:
 *
 *     FIELD OBSERVATION STORED  is not  CANONICAL PROJECTION ACCEPTED
 *
 * Storage is never refused on readiness, status, gates or authority state.
 * A REFUSED projection means the machine declined to move canonical state and
 * said why; the observation stands beside it. Rendering that as "error saving"
 * would tell a field worker their record was lost when it is safely stored.
 * ---------------------------------------------------------------------- */

export const SUBMISSION_OUTCOME = {
  STORED_AND_PROJECTED: 'STORED_AND_PROJECTED',
  STORED_NOT_PROJECTED: 'STORED_NOT_PROJECTED',
  STORED_NOTHING_TO_MOVE: 'STORED_NOTHING_TO_MOVE',
  ALREADY_STORED: 'ALREADY_STORED',
};

const OUTCOME_DISPLAY = {
  STORED_AND_PROJECTED: {
    headline: 'Recorded — and the project moved',
    tone: 'settled',
    mark: '✓',
    lead: 'Your record is stored, and OMNΛS applied it to the delivery record.',
  },
  STORED_NOT_PROJECTED: {
    headline: 'Recorded — the project did not move',
    tone: 'attention',
    mark: '!',
    lead: 'Your record is stored and stays stored. OMNΛS declined to apply it to the delivery record and said why below. Nothing you entered was lost.',
  },
  STORED_NOTHING_TO_MOVE: {
    headline: 'Recorded',
    tone: 'neutral',
    mark: '·',
    lead: 'Your record is stored. There was nothing in the delivery record for it to change.',
  },
  ALREADY_STORED: {
    headline: 'Already recorded',
    tone: 'neutral',
    mark: '=',
    lead: 'This exact record was already stored, so nothing was duplicated. What you see is the record that already existed.',
  },
};

export const submissionOutcomeDisplay = (key) =>
  OUTCOME_DISPLAY[key] || OUTCOME_DISPLAY.STORED_NOTHING_TO_MOVE;

/**
 * Read the observation POST response.
 *
 * Four things are surfaced separately because they are four different facts:
 *
 *   STORED           always true if we got a 2xx — the record exists;
 *   CREATED          false on an idempotent replay, which is not a new record;
 *   PROJECTED        whether canonical state moved;
 *   UNKNOWNS         what the backend could NOT establish while projecting.
 *
 * The last one is load-bearing and was previously dropped on the floor. When a
 * receiving record names no quantity the backend says so — "delivered_qty was
 * left as it stands" — and a screen that swallows that line lets a person
 * believe a quantity was captured.
 */
export const presentSubmissionOutcome = (response) => {
  const r =
    response && typeof response === 'object' && !Array.isArray(response)
      ? response
      : null;
  if (!r || !r.observation || typeof r.observation !== 'object') {
    return {
      readable: false,
      reason:
        'The response did not carry an observation. Nothing can be reported about it.',
      /**
       * NOT a claim that storage failed. An unreadable RESPONSE and a failed
       * WRITE are different events, and this surface must not merge them:
       * the request may well have stored the fact.
       */
      stored: null,
      display: {
        headline: 'OMNΛS answered in a shape this screen cannot read',
        tone: 'unknown',
        mark: '?',
        lead: 'Your record may or may not have been stored — this response does not say. Check the observation record before recording it again.',
      },
      unknowns: [],
    };
  }

  const created = r.created !== false;
  const projection = r.observation.projection || {};
  const state = ['PROJECTED', 'REFUSED', 'NOT_APPLICABLE'].includes(
    projection.state,
  )
    ? projection.state
    : 'NOT_APPLICABLE';

  let key;
  if (!created) key = SUBMISSION_OUTCOME.ALREADY_STORED;
  else if (state === 'PROJECTED') key = SUBMISSION_OUTCOME.STORED_AND_PROJECTED;
  else if (state === 'REFUSED') key = SUBMISSION_OUTCOME.STORED_NOT_PROJECTED;
  else key = SUBMISSION_OUTCOME.STORED_NOTHING_TO_MOVE;

  return {
    readable: true,
    reason: null,
    /** Always true on a 2xx. The write endpoint cannot refuse storage. */
    stored: true,
    created,
    projectionState: state,
    refused: state === 'REFUSED',
    /** The machine's own words at recording time. Never paraphrased. */
    projectionDetail: isText(projection.detail) ? projection.detail : null,
    outcome: key,
    display: submissionOutcomeDisplay(key),
    /** What the backend could not establish. Rendered, never swallowed. */
    unknowns: Array.isArray(r.unknowns) ? r.unknowns.filter(isText) : [],
    observationId: isText(r.observation.id) ? r.observation.id : null,
    observationType: isText(r.observation.observationType)
      ? r.observation.observationType
      : null,
  };
};

export default {
  MATERIAL_AUTHORITY_CONTRACT,
  authorityDisplay,
  isActualDelivery,
  materialStatusDisplay,
  readMaterialContract,
  readMaterialListContract,
  authoritySummary,
  orderMaterials,
  filterMaterials,
  quantityLabel,
  formatDate,
  presentSubmissionOutcome,
};
